import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

import { fetchMonitoringSettings } from 'api/monitoring';
import type { ClientMonitoringSettings } from 'api/monitoring';
import { getCriticalElementMonitoring } from 'api/generated/critical-element-monitoring/critical-element-monitoring';
import type {
  CriticalElementMonitoringDto,
  ClientGoalDto,
} from 'api/generated/schemas';
import { getClientGoalById } from 'api/generated/client-goals/client-goals';
import { getClientGoalCriticalElements } from 'api/generated/client-goal-critical-elements/client-goal-critical-elements';

import { useCurrentUser } from 'hooks/api/use-current-user';

import {
  DATE_FORMAT,
  STAGE_CONFIG,
} from 'containers/user/goals/goals-monitoring/monitoring-chart/hooks/constants';

import {
  shouldHideCE,
  shouldSkipITA,
  resolveStageKey,
} from 'helpers/utils/monitoring-visibility';
import type { CriticalElementLike } from 'helpers/utils/monitoring-visibility';

interface CurrentUserShape {
  data?: {
    oktaUserId?: string;
    client?: { id?: string | number };
  };
}

export type MonitoringSettings = ClientMonitoringSettings;

export type GoalData = ClientGoalDto;

export interface CriticalElement extends CriticalElementLike {
  id: string | number;
  summary?: string;
  details?: Array<{ category?: string; focusArea?: string }>;
}

export interface CEBlock {
  key: string;
  value?: number | null;
  previous?: number | null;
  percentageChange?: number | null;
  cohort?: number | null;
  cohortPercentageChange?: number | null;
}

export interface CEMonitoring {
  criticalElementId: string | number;
  criticalElementSummary?: string;
  details?: Array<{ category?: string; focusArea?: string }>;
  data?: CriticalElementMonitoringDto | null;
}

export interface DisplayMetric {
  criticalElementId: string | number;
  criticalElementSummary?: string;
  blocks: CEBlock[];
  isRestricted: boolean;
}

export interface UseGoalsMonitoringResult {
  loading: boolean;
  monitoringSettings: MonitoringSettings | null;
  criticalElements: CriticalElement[];
  ceMonitoringData: CEMonitoring[];
  displayMetrics: DisplayMetric[];
  currentStage: string | null;
  goalData: GoalData | null;
}

const buildBlocks = (
  data: Record<string, unknown> | null | undefined,
  fields: readonly string[]
): CEBlock[] =>
  fields.map((key) => ({
    key,
    value: data?.[key] as number | null,
    previous: data?.[`${key}Previous`] as number | null,
    percentageChange: data?.[`${key}PercentageChange`] as number | null,
    cohort: data?.[`${key}Cohort`] as number | null,
    cohortPercentageChange: data?.[`${key}CohortPercentageChange`] as
      | number
      | null,
  }));

const useGoalsMonitoring = (
  goalId: string | number | undefined,
  clientIdOverride: string | number | null = null
): UseGoalsMonitoringResult => {
  const [loading, setLoading] = useState(true);

  const [monitoringSettings, setMonitoringSettings] =
    useState<MonitoringSettings | null>(null);
  const [criticalElements, setCriticalElements] = useState<CriticalElement[]>(
    []
  );
  const [ceMonitoringData, setCEMonitoringData] = useState<CEMonitoring[]>([]);
  const [displayMetrics, setDisplayMetrics] = useState<DisplayMetric[]>([]);
  const [currentStage, setCurrentStage] = useState<string | null>(
    'inquiriesToApps'
  );
  const [goalData, setGoalData] = useState<GoalData | null>(null);

  const currentUser = useCurrentUser() as CurrentUserShape;

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      const clientId = clientIdOverride || currentUser?.data?.client?.id;

      if (!goalId || !currentUser?.data?.oktaUserId || !clientId) {
        return;
      }

      const userId = currentUser.data.oktaUserId;

      try {
        const goalRes = await getClientGoalById(
          userId,
          Number(clientId),
          Number(goalId)
        );

        setGoalData(goalRes ?? null);
      } catch (err) {
        console.error('Error fetching goal details', err);
      }

      setLoading(true);

      try {
        const monitoringSettings = await fetchMonitoringSettings(
          userId,
          clientId
        );

        setMonitoringSettings(monitoringSettings?.monitoring || null);

        const ceListRaw = await getClientGoalCriticalElements(
          userId,
          Number(goalId)
        );
        const ceList: CriticalElement[] = (ceListRaw ?? []).map((ce) => ({
          ...ce,
          id: ce.id ?? 0,
        }));

        setCriticalElements(ceList);

        const ceMonitoring = await Promise.all(
          ceList.map(async (ce) => {
            try {
              const data = await getCriticalElementMonitoring(
                userId,
                Number(ce.id)
              );

              return {
                criticalElementId: ce.id,
                criticalElementSummary: ce.summary,
                details: ce.details,
                data: data || null,
              } as CEMonitoring;
            } catch (error) {
              console.error(
                `Error fetching monitoring data for critical element ${ce.id}`,
                error
              );

              return {
                criticalElementId: ce.id,
                data: null,
              } as CEMonitoring;
            }
          })
        );

        setCEMonitoringData(ceMonitoring);

        if (monitoringSettings?.monitoring) {
          const { inquiriesToApps, applicationsToAdmits, admitsToEnrollment } =
            monitoringSettings.monitoring;

          const stageKey = resolveStageKey(
            { inquiriesToApps, applicationsToAdmits, admitsToEnrollment },
            DATE_FORMAT
          );

          if (stageKey) {
            const metrics = ceMonitoring
              .filter((ce) => ce.data && !shouldHideCE(ce as never))
              .map<DisplayMetric>((ce) => {
                const stageFields = STAGE_CONFIG[stageKey].fields;
                const isRestricted =
                  stageKey === 'inquiriesToApps' && shouldSkipITA(ce as never);

                return {
                  criticalElementId: ce.criticalElementId,
                  criticalElementSummary: ce.criticalElementSummary,
                  blocks: buildBlocks(
                    ce.data as Record<string, unknown> | null | undefined,
                    stageFields
                  ),
                  isRestricted,
                };
              });

            setDisplayMetrics(metrics);
            setCurrentStage(stageKey);
          } else {
            setDisplayMetrics([]);
            setCurrentStage(null);
          }
        }
      } catch (error) {
        console.error('Error fetching goals monitoring data', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [goalId, currentUser?.data?.oktaUserId, clientIdOverride]);

  return {
    loading,
    monitoringSettings,
    criticalElements,
    ceMonitoringData,
    displayMetrics,
    currentStage,
    goalData,
  };
};

export default useGoalsMonitoring;
